import { FC, useContext, useState } from 'react';

import { useQuery } from '@apollo/client';
import { ClockIcon, Preloader } from '@smartshell/ui-kit';
import cn from 'classnames';

import { LINKS } from 'links';
import { LocalizationContext } from 'localization';
import {
  GET_CURRENT_BENEFICIARY,
  IGetCurrentBeneficiaryResponse,
  SettingsContext,
} from 'settings';

import { IntegrationCard, Modals, TModal } from './components';
import { cashBoxDescription, SBPDescription } from './constants';
import s from './IntegrationsTab.module.scss';

export const IntegrationsTab: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    state: {
      settings: { countryCode },
    },
  } = useContext(SettingsContext);
  const [typeModal, setTypeModal] = useState<null | TModal>(null);

  const { data, loading } = useQuery<IGetCurrentBeneficiaryResponse>(
    GET_CURRENT_BENEFICIARY,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const closeModal = (): void => {
    setTypeModal(null);
  };

  return (
    <>
      <Modals type={typeModal} close={closeModal} />
      <div className={s.frame}>
        <Preloader loading={loading} className={s.preloader}>
          <div className={cn(s.container, s[countryCode.toLowerCase()])}>
            <div className={s.telegram}>
              <IntegrationCard
                title="Telegram"
                featuresFlag="telegram_notify"
                setting="telegramChannelId"
                description={t('settings.integration.telegram.description')}
                link={LINKS.telegram_notify[lang]}
                action={() => setTypeModal('TELEGRAM')}
              />
            </div>

            <div className={s.cash_box}>
              <IntegrationCard
                title="Касса"
                featuresFlag="cash_box"
                setting="isCashboxEnabled"
                description={cashBoxDescription}
                link={LINKS.cash_box[lang]}
                action={() => setTypeModal('CASH_BOX')}
                isCountry={countryCode === 'RU'}
              />
            </div>

            <div className={s.sbp}>
              <IntegrationCard
                title="Онлайн-СБП"
                featuresFlag="sbp_tinkoff"
                setting="tinkoffSbpEnabled"
                description={<SBPDescription />}
                link={LINKS.sbp_tinkoff[lang]}
                action={() => setTypeModal('TINKOFF')}
                status={data?.getCurrentBeneficiary?.status}
                beneficiary={data?.getCurrentBeneficiary}
                isCountry={countryCode === 'RU'}
              />
            </div>

            <div className={s.cloudPayments}>
              <IntegrationCard
                title={t('report.total.cloud_payments')}
                featuresFlag="client_transaction"
                setting="cloudPayments"
                description={t(
                  'settings.integration.cloudPayments.description'
                )}
                link={LINKS.cloudPayments[lang]}
                action={() => setTypeModal('CLOUDPAYMENTS')}
              />
            </div>

            <div className={s.kaspi_pos}>
              <IntegrationCard
                title="Kaspi POS"
                description={t('settings.integration.kaspiPOS.description')}
                // link={LINKS.kaspi_POS}
                soon
                isCountry={countryCode === 'KZ'}
              />
            </div>

            <div className={s.kaspi_online}>
              <IntegrationCard
                title={t('settings.integration.kaspiOnline.title')}
                description={t('settings.integration.kaspiOnline.description')}
                link={LINKS.kaspi_online[lang]}
                action={() => setTypeModal('KASPI_ONLINE')}
                featuresFlag="kaspi_online"
                setting="kaspiOnlineEnabled"
                isCountry={countryCode === 'KZ'}
              />
            </div>

            <div className={s.stripe}>
              <IntegrationCard
                title="Stripe"
                description={t('settings.integration.stripe.description')}
                link={LINKS.stripe[lang]}
                action={() => setTypeModal('STRIPE')}
                setting="stripeEnabled"
                isCountry={!['KZ', 'RU', 'VI'].includes(countryCode)}
                featuresFlag="stripe"
              />
            </div>

            <div className={s.BePaid}>
              <IntegrationCard
                title="bePaid"
                description={t('settings.integration.stripe.description')}
                link={LINKS.BePaid[lang]}
                action={() => setTypeModal('BE_PAID')}
                setting="bePaidEnabled"
                isCountry={countryCode === 'BY'}
                featuresFlag="bepaid"
              />
            </div>

            <div className={s.announce}>
              <ClockIcon color="violet" />
              <span className={s.text}>{t('integration.announce.text')}</span>
            </div>
          </div>
        </Preloader>
      </div>
    </>
  );
};
